import { useCallback, useState, type FormEvent } from "react";
import type { OrgProfile, Organization, PostalAddress } from "@backstory/protocol";
import { api } from "../api";
import { Logo, Wordmark } from "../components/Logo";
import { BusyButton, Field } from "../components/ui";
import { OrgProfileFields, browserTimezone, normalizeProfile, profileProblems, useAddressCheck } from "../routes/org/OrgProfileFields";
import { authStore } from "./store";

/** A profile is complete only when every required contact and address field is valid. */
export function organizationSetupComplete(org: Organization | undefined): boolean {
  return !!org?.name.trim() && Object.keys(profileProblems(org.profile ?? {})).length === 0;
}

/**
 * Blocks the product shell until the current organization has the contact and
 * address information needed for account notices and billing.
 */
export function OrgSetupGate({ org }: { org: Organization }) {
  const canManage = org.role === "owner" || org.role === "admin";
  const [name, setName] = useState(org.name);
  const [profile, setProfile] = useState<OrgProfile>({
    ...(org.profile ?? {}),
    contactEmail: org.profile?.contactEmail || authStore.get().me?.user.email || "",
    country: org.profile?.country || "US",
    timezone: org.profile?.timezone || browserTimezone(),
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [attempted, setAttempted] = useState(false);
  const problems = profileProblems(profile);
  const invalid = !name.trim() || Object.keys(problems).length > 0;
  const address = useAddressCheck(useCallback((value: PostalAddress) => api.orgs.lookupAddress(org.id, value), [org.id]));

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    setError(undefined);
    if (invalid) return;
    const chosen = await address.confirm(profile);
    if (!chosen) return;
    setProfile(chosen);
    setBusy(true);
    try {
      await api.orgs.patch(org.id, { name: name.trim(), profile: normalizeProfile(chosen) });
      await authStore.load();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const shown = attempted ? problems : {};

  return (
    <div className="auth org-setup">
      <div className="auth-rings" aria-hidden="true" />
      <Logo size={420} className="auth-mark" />
      <div className="org-setup-card card">
        <div className="brand">
          <Wordmark size={22} />
        </div>
        <h1>{canManage ? "Set up your organization" : "Organization setup required"}</h1>
        <p className="lead">
          {canManage
            ? "Add the contact and address for this organization. You can change them later in settings."
            : "An owner or admin has to finish the contact and address before this organization can be used."}
        </p>
        {canManage ? (
          <form onSubmit={(event) => void save(event)} autoComplete="off">
            {error && <div className="error-box" role="alert">{error}</div>}
            <Field label="Organization name" error={attempted && !name.trim() ? "Enter an organization name." : undefined}>
              {(props) => <input {...props} className="input" value={name} onChange={(event) => setName(event.target.value)} autoComplete="organization" autoFocus />}
            </Field>
            <OrgProfileFields value={profile} onChange={setProfile} errors={shown} />
            <BusyButton className="btn primary block" type="submit" busy={busy || address.checking} busyLabel={address.checking ? "Checking address…" : "Saving…"} disabled={invalid}>
              Continue
            </BusyButton>
            {address.dialog}
          </form>
        ) : (
          <button className="btn block" type="button" onClick={() => void authStore.logout()}>
            Sign out
          </button>
        )}
      </div>
    </div>
  );
}
